import { useState, useEffect, createContext, useContext, type ReactNode } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './useAuth'

interface PortalUser {
  id: number
  cliente_id: number
  ativo: boolean
  acesso_universal: boolean
  clientes: {
    nome_completo: string | null
    nome_fantasia: string | null
    email: string | null
  } | null
}

interface ClienteAuthContextValue {
  portalUser: PortalUser | null
  nomeCliente: string
  loading: boolean
  acesso_universal: boolean
}

const ClienteAuthContext = createContext<ClienteAuthContextValue | null>(null)

export function ClienteAuthProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth()
  const [portalUser, setPortalUser] = useState<PortalUser | null>(null)
  const [loading,    setLoading]    = useState(true)

  useEffect(() => {
    if (authLoading) return
    if (!user) { setPortalUser(null); setLoading(false); return }

    let cancelled = false

    async function resolve() {
      // 1. Busca por auth_user_id (fluxo normal)
      const { data } = await supabase
        .from('portal_users')
        .select(`id, cliente_id, ativo, acesso_universal, clientes ( nome_completo, nome_fantasia, email )`)
        .eq('auth_user_id', user.id)
        .eq('ativo', true)
        .maybeSingle()

      // Se não existe portal_user para este auth.uid(), acesso negado.
      // portal_user só deve existir se criado via inviteUserByEmail pelo escritório.
      if (!cancelled) { setPortalUser(data ?? null); setLoading(false) }
    }

    resolve()
    return () => { cancelled = true }
  }, [user, authLoading])

  const nomeCliente = portalUser?.clientes?.nome_completo
    || portalUser?.clientes?.nome_fantasia
    || ''

  const acesso_universal = portalUser?.acesso_universal || false

  return (
    <ClienteAuthContext.Provider value={{ portalUser, nomeCliente, loading, acesso_universal }}>
      {children}
    </ClienteAuthContext.Provider>
  )
}

// Seguro para uso fora do provider (ex: preview mode interno do escritório)
export function useClienteAuth(): ClienteAuthContextValue {
  const ctx = useContext(ClienteAuthContext)
  if (!ctx) return { portalUser: null, nomeCliente: '', loading: false, acesso_universal: false }
  return ctx
}
